import React from "react";
import {
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import SidebarAdmin from "../../menu/SidebarAdmin";
import Topbar from "../../menu/Topbar";
import Api from "../../Services/Api";
import { useAuth } from "../../auth/AuthContext";
import { isSuperAdmin } from "../../../utils/accessControl";
import {
  APPROVAL_STATUS,
  EMPTY_FORM,
  formatCnpj,
  formatPhone,
  formatZipCode,
  getApprovalStatusMeta,
  mapClientToForm,
  shortenText,
} from "../../realgems/realgemsClientHelpers";
import "./AdminRealgemsClients.css";

const PAGE_SIZE = 50;

const tableCellSx = {
  borderBottom: "1px solid #eef2f7",
  color: "#334155",
  fontSize: "0.88rem",
  py: 1.55,
  whiteSpace: "nowrap",
};

const headCellSx = {
  borderBottom: "1px solid #e2e8f0",
  color: "#64748b",
  fontSize: "0.78rem",
  fontWeight: 800,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  backgroundColor: "#f8fafc",
};

const AdminRealgemsClients = () => {
  const { user } = useAuth();
  const canDeleteClients = isSuperAdmin(user?.access_type);
  const [menuClose, setMenuClose] = React.useState(false);
  const [clients, setClients] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const [reloadKey, setReloadKey] = React.useState(0);
  const [search, setSearch] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pagination, setPagination] = React.useState({
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 1,
  });
  const [viewingClient, setViewingClient] = React.useState(null);
  const [editingClient, setEditingClient] = React.useState(null);
  const [editForm, setEditForm] = React.useState(EMPTY_FORM);
  const [editSubmitting, setEditSubmitting] = React.useState(false);
  const [editError, setEditError] = React.useState("");
  const [approvalClient, setApprovalClient] = React.useState(null);
  const [approvalStatus, setApprovalStatus] = React.useState("");
  const [approvalNotes, setApprovalNotes] = React.useState("");
  const [approvalSubmitting, setApprovalSubmitting] = React.useState(false);
  const [approvalError, setApprovalError] = React.useState("");
  const [deletingClient, setDeletingClient] = React.useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState("");

  const toggleMenu = () => setMenuClose((prev) => !prev);

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 250);

    return () => window.clearTimeout(timer);
  }, [search]);

  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setViewingClient(null);
        setEditingClient(null);
        setApprovalClient(null);
        if (!deleteSubmitting) setDeletingClient(null);
        setEditError("");
        setApprovalError("");
        setDeleteError("");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [deleteSubmitting]);

  React.useEffect(() => {
    let active = true;

    const loadClients = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await Api.get("/admin/realgems/clients", {
          params: {
            page: currentPage,
            pageSize: PAGE_SIZE,
            search: debouncedSearch || undefined,
          },
        });

        if (!active) return;

        setClients(Array.isArray(response?.data?.items) ? response.data.items : []);
        setCurrentPage(Number(response?.data?.pagination?.page || currentPage || 1));
        setPagination({
          pageSize: Number(response?.data?.pagination?.pageSize || PAGE_SIZE),
          total: Number(response?.data?.pagination?.total || 0),
          totalPages: Number(response?.data?.pagination?.totalPages || 1),
        });
      } catch (err) {
        if (!active) return;

        console.error("Erro ao carregar clientes Realgems no admin:", err);
        setError(err?.response?.data?.message || "Não foi possível carregar os clientes.");
        setClients([]);
        setPagination((current) => ({
          pageSize: current.pageSize,
          total: 0,
          totalPages: 1,
        }));
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadClients();

    return () => {
      active = false;
    };
  }, [currentPage, debouncedSearch, reloadKey]);

  const replaceClientInState = React.useCallback((updatedClient) => {
    if (!updatedClient?.id) return;

    setClients((current) =>
      current.map((item) => (Number(item.id) === Number(updatedClient.id) ? updatedClient : item))
    );
  }, []);

  const closeEditDrawer = () => {
    setEditingClient(null);
    setEditForm(EMPTY_FORM);
    setEditError("");
  };

  const closeViewModal = () => {
    setViewingClient(null);
  };

  const closeApprovalModal = () => {
    setApprovalClient(null);
    setApprovalStatus("");
    setApprovalNotes("");
    setApprovalError("");
  };

  const closeDeleteModal = () => {
    if (deleteSubmitting) return;
    setDeletingClient(null);
    setDeleteError("");
  };

  const openEditDrawer = (client) => {
    setViewingClient(null);
    setApprovalClient(null);
    setEditingClient(client);
    setEditForm(mapClientToForm(client));
    setEditError("");
  };

  const openApprovalModal = (client) => {
    setViewingClient(null);
    setEditingClient(null);
    setApprovalClient(client);
    setApprovalStatus(
      client.approvalStatus === APPROVAL_STATUS.PENDING ? "" : client.approvalStatus || ""
    );
    setApprovalNotes(client.approvalNotes || "");
    setApprovalError("");
  };

  const openDeleteModal = (client) => {
    setViewingClient(null);
    setEditingClient(null);
    setApprovalClient(null);
    setDeletingClient(client);
    setDeleteError("");
    setSuccess("");
  };

  const handleEditFieldChange = (field) => (event) => {
    const rawValue = event.target.value;

    setEditForm((current) => {
      if (field === "cnpj") return { ...current, cnpj: formatCnpj(rawValue) };
      if (field === "zipCode") return { ...current, zipCode: formatZipCode(rawValue) };
      if (field === "phone") return { ...current, phone: formatPhone(rawValue) };
      return { ...current, [field]: rawValue };
    });

    setEditError("");
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    if (!editingClient?.id) return;

    try {
      setEditSubmitting(true);
      setEditError("");

      const response = await Api.put(`/admin/realgems/clients/${editingClient.id}`, editForm);
      if (response?.data?.item) {
        replaceClientInState(response.data.item);
      }

      closeEditDrawer();
    } catch (err) {
      console.error("Erro ao editar cliente Realgems no admin:", err);
      setEditError(err?.response?.data?.message || "Não foi possível salvar o cliente.");
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleApprovalSubmit = async (event) => {
    event.preventDefault();
    if (!approvalClient?.id) return;

    try {
      setApprovalSubmitting(true);
      setApprovalError("");

      const response = await Api.patch(`/admin/realgems/clients/${approvalClient.id}/approval`, {
        approvalStatus,
        approvalNotes,
      });

      if (response?.data?.item) {
        replaceClientInState(response.data.item);
      }

      closeApprovalModal();
    } catch (err) {
      console.error("Erro ao atualizar aprovação do cliente Realgems:", err);
      setApprovalError(
        err?.response?.data?.message || "Não foi possível atualizar a aprovação do cadastro."
      );
    } finally {
      setApprovalSubmitting(false);
    }
  };

  const handleDeleteClient = async () => {
    if (!deletingClient?.id || !canDeleteClients) return;

    try {
      setDeleteSubmitting(true);
      setDeleteError("");
      setError("");
      setSuccess("");

      const response = await Api.delete(`/admin/realgems/clients/${deletingClient.id}`);
      const deletedId = Number(response?.data?.deletedId || deletingClient.id);

      setClients((current) => current.filter((item) => Number(item.id) !== deletedId));
      setPagination((current) => ({
        ...current,
        total: Math.max(0, current.total - 1),
        totalPages: Math.max(1, Math.ceil(Math.max(0, current.total - 1) / current.pageSize)),
      }));
      setSuccess(response?.data?.message || "Cliente excluído com sucesso.");
      setDeletingClient(null);

      if (clients.length === 1 && currentPage > 1) {
        setCurrentPage((page) => Math.max(1, page - 1));
      } else {
        setReloadKey((value) => value + 1);
      }
    } catch (err) {
      console.error("Erro ao excluir cliente Realgems:", err);
      setDeleteError(err?.response?.data?.message || "Não foi possível excluir o cliente.");
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const renderEditFormFields = () => (
    <>
      <label className="admin-realgems-field">
        <span>Razão Social</span>
        <input value={editForm.companyLegalName} onChange={handleEditFieldChange("companyLegalName")} />
      </label>

      <label className="admin-realgems-field">
        <span>Nome Fantasia</span>
        <input value={editForm.tradeName} onChange={handleEditFieldChange("tradeName")} />
      </label>

      <label className="admin-realgems-field">
        <span>CNPJ</span>
        <input
          value={editForm.cnpj}
          onChange={handleEditFieldChange("cnpj")}
          placeholder="00.000.000/0000-00"
          inputMode="numeric"
        />
      </label>

      <label className="admin-realgems-field">
        <span>Data de Fundação</span>
        <input
          type="date"
          value={editForm.foundationDate}
          onChange={handleEditFieldChange("foundationDate")}
        />
      </label>

      <label className="admin-realgems-field">
        <span>Inscrição Estadual</span>
        <input value={editForm.stateRegistration} onChange={handleEditFieldChange("stateRegistration")} />
      </label>

      <label className="admin-realgems-field">
        <span>Segmento de Atuação</span>
        <input value={editForm.businessSegment} onChange={handleEditFieldChange("businessSegment")} />
      </label>

      <label className="admin-realgems-field">
        <span>CNAE Principal</span>
        <input value={editForm.primaryCnae} onChange={handleEditFieldChange("primaryCnae")} />
      </label>

      <label className="admin-realgems-field admin-realgems-field-wide">
        <span>Endereço Completo</span>
        <input value={editForm.fullAddress} onChange={handleEditFieldChange("fullAddress")} />
      </label>

      <label className="admin-realgems-field">
        <span>Cidade/UF</span>
        <input value={editForm.cityState} onChange={handleEditFieldChange("cityState")} />
      </label>

      <label className="admin-realgems-field">
        <span>CEP</span>
        <input
          value={editForm.zipCode}
          onChange={handleEditFieldChange("zipCode")}
          placeholder="00000-000"
          inputMode="numeric"
        />
      </label>

      <label className="admin-realgems-field">
        <span>Telefone</span>
        <input
          value={editForm.phone}
          onChange={handleEditFieldChange("phone")}
          placeholder="(11) 99999-9999"
          inputMode="tel"
        />
      </label>

      <label className="admin-realgems-field">
        <span>E-mail Corporativo</span>
        <input
          type="email"
          value={editForm.corporateEmail}
          onChange={handleEditFieldChange("corporateEmail")}
        />
      </label>

      <label className="admin-realgems-field">
        <span>Site</span>
        <input value={editForm.website} onChange={handleEditFieldChange("website")} />
      </label>

      <label className="admin-realgems-field">
        <span>Instagram / Redes Sociais</span>
        <input value={editForm.socialMedia} onChange={handleEditFieldChange("socialMedia")} />
      </label>

      <label className="admin-realgems-field admin-realgems-field-wide">
        <span>Observação do usuário</span>
        <textarea
          value={editForm.clientNotes}
          onChange={handleEditFieldChange("clientNotes")}
          placeholder="Preencha se houver alguma observação importante sobre o cliente."
          rows={4}
        />
      </label>
    </>
  );

  const hasClients = clients.length > 0;

  return (
    <div className="app-container admin-realgems-page">
      <SidebarAdmin close={menuClose} realgemsClientsActive="active" />

      <main className="main-content admin-realgems-main-content">
        <Topbar page="Clientes Realgems" onToggleMenu={toggleMenu} />

        <section className="admin-realgems-panel">
          <div className="admin-realgems-panel-header">
            <div>
              <span className="admin-realgems-kicker">Realgems</span>
              <h1>Clientes cadastrados</h1>
              <p>
                Acompanhe todos os clientes cadastrados no portal Realgems, com o usuário responsável por
                cada registro.
              </p>
            </div>
          </div>

          <div className="admin-realgems-toolbar">
            <input
              type="search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setCurrentPage(1);
              }}
              placeholder="Buscar por nome, CNPJ, cidade, e-mail, telefone, usuário ou status"
              className="admin-realgems-search"
            />
          </div>

          <div className="admin-realgems-summary">
            {loading
              ? "Carregando clientes..."
              : `${pagination.total} cliente(s) encontrado(s). Página ${currentPage} de ${pagination.totalPages}.`}
          </div>

          {error ? <div className="admin-realgems-message error">{error}</div> : null}
          {success ? <div className="admin-realgems-message success">{success}</div> : null}

          {loading ? (
            <div className="admin-realgems-empty">Carregando clientes...</div>
          ) : hasClients ? (
            <>
              <TableContainer component={Paper} className="admin-realgems-table-wrap">
                <Table sx={{ minWidth: canDeleteClients ? 1210 : 1080 }} aria-label="Tabela administrativa de clientes Realgems">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headCellSx}>Nome Fantasia</TableCell>
                      <TableCell sx={headCellSx}>CNPJ</TableCell>
                      <TableCell sx={headCellSx}>Cadastrado por</TableCell>
                      <TableCell sx={headCellSx}>Status</TableCell>
                      <TableCell sx={headCellSx}>Observação</TableCell>
                      <TableCell sx={{ ...headCellSx, textAlign: "right" }}>Ações</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {clients.map((client) => {
                      const statusMeta = getApprovalStatusMeta(client.approvalStatus);

                      return (
                        <TableRow key={client.id} hover>
                          <TableCell sx={tableCellSx}>
                            <strong className="admin-realgems-table-strong">{client.tradeName}</strong>
                          </TableCell>
                          <TableCell sx={tableCellSx}>{formatCnpj(client.cnpj)}</TableCell>
                          <TableCell sx={tableCellSx}>{client.ownerUserName || "Não informado"}</TableCell>
                          <TableCell sx={tableCellSx}>
                            <Chip
                              label={statusMeta.label}
                              size="small"
                              className={`admin-realgems-status-badge ${statusMeta.className}`}
                            />
                          </TableCell>
                          <TableCell sx={{ ...tableCellSx, whiteSpace: "normal", minWidth: 250 }}>
                            {client.approvalStatus === "REJECTED" && client.approvalNotes
                              ? shortenText(client.approvalNotes, 120)
                              : client.approvalStatus === "PENDING" && client.changedFields?.length
                              ? `Alterado: ${client.changedFields.map((field) => field.label).join(", ")}`
                              : "-"}
                          </TableCell>
                          <TableCell sx={tableCellSx} align="right">
                            <div className="admin-realgems-table-actions">
                              <button
                                type="button"
                                className="admin-realgems-btn secondary"
                                onClick={() => setViewingClient(client)}
                              >
                                Ver cliente
                              </button>
                              <button
                                type="button"
                                className="admin-realgems-btn secondary"
                                onClick={() => openEditDrawer(client)}
                              >
                                Editar
                              </button>
                              <button
                                type="button"
                                className="admin-realgems-btn primary"
                                onClick={() => openApprovalModal(client)}
                              >
                                Analisar
                              </button>
                              {canDeleteClients ? (
                                <button
                                  type="button"
                                  className="admin-realgems-btn danger"
                                  onClick={() => openDeleteModal(client)}
                                >
                                  Excluir
                                </button>
                              ) : null}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {pagination.totalPages > 1 ? (
                <div className="admin-realgems-pagination">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    disabled={currentPage <= 1}
                  >
                    Anterior
                  </button>

                  <span>
                    Página {currentPage} de {pagination.totalPages}
                  </span>

                  <button
                    type="button"
                    onClick={() => setCurrentPage((page) => Math.min(pagination.totalPages, page + 1))}
                    disabled={currentPage >= pagination.totalPages}
                  >
                    Próxima
                  </button>
                </div>
              ) : null}
            </>
          ) : (
            <div className="admin-realgems-empty">
              {debouncedSearch
                ? "Nenhum cliente encontrado com esse filtro."
                : "Nenhum cliente Realgems cadastrado ainda."}
            </div>
          )}
        </section>

        <div
          className={`admin-realgems-modal-backdrop ${deletingClient ? "is-open" : ""}`}
          onClick={closeDeleteModal}
          aria-hidden={!deletingClient}
        />

        <section
          className={`admin-realgems-modal ${deletingClient ? "is-open" : ""}`}
          aria-hidden={!deletingClient}
          aria-modal="true"
          role="dialog"
          aria-labelledby="delete-realgems-client-title"
        >
          <div className="admin-realgems-modal-card admin-realgems-delete-card">
            <div className="admin-realgems-modal-header">
              <div>
                <span className="admin-realgems-kicker danger">Exclusão permanente</span>
                <h2 id="delete-realgems-client-title">Excluir cliente</h2>
              </div>
            </div>

            {deletingClient ? (
              <div className="admin-realgems-delete-content">
                <p>
                  Tem certeza de que deseja excluir o cadastro de{" "}
                  <strong>{deletingClient.tradeName || deletingClient.companyLegalName}</strong>?
                </p>
                <div className="admin-realgems-delete-warning">
                  Esta ação não pode ser desfeita. Para preservar o histórico, clientes que
                  possuam pedidos vinculados não poderão ser excluídos.
                </div>

                {deleteError ? (
                  <div className="admin-realgems-message error">{deleteError}</div>
                ) : null}

                <div className="admin-realgems-delete-actions">
                  <button
                    type="button"
                    className="admin-realgems-btn secondary"
                    onClick={closeDeleteModal}
                    disabled={deleteSubmitting}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="admin-realgems-btn danger"
                    onClick={handleDeleteClient}
                    disabled={deleteSubmitting}
                  >
                    {deleteSubmitting ? "Excluindo..." : "Excluir definitivamente"}
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </section>

        <div
          className={`admin-realgems-modal-backdrop ${viewingClient ? "is-open" : ""}`}
          onClick={closeViewModal}
          aria-hidden={!viewingClient}
        />

        <section className={`admin-realgems-modal ${viewingClient ? "is-open" : ""}`} aria-hidden={!viewingClient}>
          <div className="admin-realgems-modal-card">
            <div className="admin-realgems-modal-header">
              <div>
                <span className="admin-realgems-kicker">Cliente</span>
                <h2>Visualizar cliente</h2>
              </div>

              <button type="button" className="admin-realgems-btn secondary" onClick={closeViewModal}>
                Fechar
              </button>
            </div>

            {viewingClient ? (
              <div className="admin-realgems-view-grid">
                <div><strong>Status:</strong> {getApprovalStatusMeta(viewingClient.approvalStatus).label}</div>
                <div><strong>Cadastrado por:</strong> {viewingClient.ownerUserName || "Não informado"}</div>
                <div><strong>Nome Fantasia:</strong> {viewingClient.tradeName}</div>
                <div><strong>Razão Social:</strong> {viewingClient.companyLegalName}</div>
                <div><strong>CNPJ:</strong> {formatCnpj(viewingClient.cnpj)}</div>
                <div><strong>Data de Fundação:</strong> {viewingClient.foundationDate || "-"}</div>
                <div><strong>Inscrição Estadual:</strong> {viewingClient.stateRegistration}</div>
                <div><strong>Segmento de Atuação:</strong> {viewingClient.businessSegment}</div>
                <div><strong>CNAE Principal:</strong> {viewingClient.primaryCnae}</div>
                <div><strong>Endereço Completo:</strong> {viewingClient.fullAddress}</div>
                <div><strong>Cidade/UF:</strong> {viewingClient.cityState}</div>
                <div><strong>CEP:</strong> {viewingClient.zipCode}</div>
                <div><strong>Telefone:</strong> {viewingClient.phone}</div>
                <div><strong>E-mail Corporativo:</strong> {viewingClient.corporateEmail}</div>
                <div><strong>Site:</strong> {viewingClient.website}</div>
                <div><strong>Instagram / Redes Sociais:</strong> {viewingClient.socialMedia}</div>
                <div className="admin-realgems-view-grid-wide">
                  <strong>Observação do usuário:</strong> {viewingClient.clientNotes || "-"}
                </div>
                <div className="admin-realgems-view-grid-wide">
                  <strong>Observação da aprovação:</strong> {viewingClient.approvalNotes || "-"}
                </div>
                {viewingClient.changedFields?.length ? (
                  <div className="admin-realgems-view-grid-wide admin-realgems-changes">
                    <strong>Campos alterados para nova conferência:</strong>
                    {viewingClient.changedFields.map((field) => (
                      <div key={field.field} className="admin-realgems-change-row">
                        <span>{field.label}</span>
                        <del>{field.before || "-"}</del>
                        <span>{field.after || "-"}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </section>

        <div
          className={`admin-realgems-drawer-backdrop ${editingClient ? "is-open" : ""}`}
          onClick={closeEditDrawer}
          aria-hidden={!editingClient}
        />

        <aside className={`admin-realgems-drawer ${editingClient ? "is-open" : ""}`} aria-hidden={!editingClient}>
          <div className="admin-realgems-drawer-header">
            <div>
              <span className="admin-realgems-kicker">Edição</span>
              <h2>Editar cliente</h2>
            </div>

            <button type="button" className="admin-realgems-btn secondary" onClick={closeEditDrawer}>
              Fechar
            </button>
          </div>

          <form className="admin-realgems-form" onSubmit={handleEditSubmit}>
            {renderEditFormFields()}

            <div className="admin-realgems-form-actions">
              <button type="submit" className="admin-realgems-btn primary" disabled={editSubmitting}>
                {editSubmitting ? "Salvando..." : "Salvar alterações"}
              </button>
            </div>

            {editError ? <div className="admin-realgems-message error">{editError}</div> : null}
          </form>
        </aside>

        <div
          className={`admin-realgems-modal-backdrop ${approvalClient ? "is-open" : ""}`}
          onClick={closeApprovalModal}
          aria-hidden={!approvalClient}
        />

        <section className={`admin-realgems-modal ${approvalClient ? "is-open" : ""}`} aria-hidden={!approvalClient}>
          <div className="admin-realgems-modal-card">
            <div className="admin-realgems-modal-header">
              <div>
                <h2>Analisar cadastro</h2>
                {approvalClient ? (
                  <p>
                    Defina o status do cadastro de <strong>{approvalClient.tradeName}</strong>.
                  </p>
                ) : null}
              </div>

              <button type="button" className="admin-realgems-btn secondary" onClick={closeApprovalModal}>
                Fechar
              </button>
            </div>

            <form onSubmit={handleApprovalSubmit} className="admin-realgems-approval-form">
              {approvalClient?.changedFields?.length ? (
                <div className="admin-realgems-changes">
                  <strong>Campos alterados para nova conferência</strong>
                  {approvalClient.changedFields.map((field) => (
                    <div key={field.field} className="admin-realgems-change-row">
                      <span>{field.label}</span>
                      <del>{field.before || "-"}</del>
                      <span>{field.after || "-"}</span>
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="admin-realgems-approval-options">
                {[APPROVAL_STATUS.APPROVED, APPROVAL_STATUS.REJECTED].map((status) => {
                  const meta = getApprovalStatusMeta(status);

                  return (
                    <label
                      key={status}
                      className={`admin-realgems-approval-option ${
                        approvalStatus === status ? "selected" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="approvalStatus"
                        value={status}
                        checked={approvalStatus === status}
                        onChange={() => setApprovalStatus(status)}
                        required
                      />
                      <span className={`admin-realgems-status-badge ${meta.className}`}>{meta.label}</span>
                    </label>
                  );
                })}
              </div>

              <label className="admin-realgems-approval-notes">
                <span>Observação</span>
                <textarea
                  value={approvalNotes}
                  onChange={(event) => {
                    setApprovalNotes(event.target.value);
                    setApprovalError("");
                  }}
                  placeholder="Descreva o motivo da recusa ou alguma observação sobre a análise."
                  rows={5}
                />
              </label>

              <div className="admin-realgems-form-actions">
                <button type="submit" className="admin-realgems-btn primary" disabled={approvalSubmitting}>
                  {approvalSubmitting ? "Salvando..." : "Salvar"}
                </button>
              </div>

              {approvalError ? <div className="admin-realgems-message error">{approvalError}</div> : null}
            </form>
          </div>
        </section>
      </main>
    </div>
  );
};

export default AdminRealgemsClients;
